import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";

export default function Cart() {
  const { cart, updateQty, removeItem, subtotal } = useApp();
  const nav = useNavigate();
  const shipping = 0;
  const total = subtotal + shipping;

  if (!cart.length) {
    return (
      <div data-testid="cart-empty" className="mx-auto max-w-3xl px-4 py-24 text-center">
        <ShoppingBag className="h-12 w-12 mx-auto text-white/30 mb-4"/>
        <h1 className="font-display text-3xl mb-2">Your cart is empty</h1>
        <p className="text-white/60 mb-6">Add displays from the shop to get started.</p>
        <Button asChild data-testid="cart-shop-cta" className="bg-blue-600 hover:bg-blue-500 rounded-none">
          <Link to="/shop">Continue shopping</Link>
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight mb-8">Your cart</h1>
      <div className="grid gap-6 lg:grid-cols-[1fr,340px]">
        <div className="border border-white/10 bg-zinc-950 divide-y divide-white/10">
          {cart.map((c, i) => (
            <div key={`${c.product_id}-${c.variant_id}`} data-testid={`cart-item-${i}`} className="flex gap-4 p-4">
              <img src={c.image_url} className="h-24 w-24 object-cover border border-white/10" alt={c.title}/>
              <div className="flex-1 min-w-0">
                <p className="font-display text-lg leading-tight truncate">{c.title}</p>
                <p className="text-xs text-white/60 font-mono mt-1">{c.variant_name}</p>
                <p className="font-mono text-sm mt-2">{fmtINR(c.price_inr)}</p>
              </div>
              <div className="flex flex-col items-end justify-between">
                <button data-testid={`remove-${i}`} onClick={() => removeItem(i)} className="text-white/40 hover:text-red-400 transition-colors">
                  <Trash2 className="h-4 w-4"/>
                </button>
                <div className="flex items-center border border-white/10">
                  <button onClick={() => updateQty(i, c.qty - 1)} className="p-2 hover:bg-white/5"><Minus className="h-3 w-3"/></button>
                  <span className="px-3 text-sm font-mono">{c.qty}</span>
                  <button onClick={() => updateQty(i, c.qty + 1)} className="p-2 hover:bg-white/5"><Plus className="h-3 w-3"/></button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <aside className="border border-white/10 bg-zinc-950 p-5 h-fit">
          <p className="mono-label text-white/50 mb-4">Order summary</p>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-white/60">Subtotal</span><span className="font-mono">{fmtINR(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-white/60">Shipping</span><span className="font-mono text-emerald-400">FREE</span></div>
            <div className="border-t border-white/10 my-2"/>
            <div className="flex justify-between text-base"><span>Total</span><span data-testid="cart-total" className="font-mono text-xl">{fmtINR(total)}</span></div>
          </div>
          <Button data-testid="checkout-btn" onClick={() => nav("/checkout")} className="w-full mt-5 bg-blue-600 hover:bg-blue-500 rounded-none">
            Proceed to checkout
          </Button>
        </aside>
      </div>
    </div>
  );
}
